import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SettingsLayout from '../../components/SettingsLayout';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Separator } from '../../components/ui/separator';
import { API_URL } from '../../lib/apiBase';
import {
  Bot,
  Slack,
  MessagesSquare,
  Mail,
  Lock,
  Bell,
  CheckCircle2,
  ArrowRight,
  Lightbulb,
  Plus,
} from 'lucide-react';

const getVisitorId = () => {
  const KEY = 'vibesprint_visitor_id';
  let v = localStorage.getItem(KEY);
  if (!v) {
    v = `vis_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(KEY, v);
  }
  return v;
};

const COMING_SOON = [
  {
    key: 'slack',
    name: 'Slack',
    icon: Slack,
    description: 'Get notified in Slack when new Build or churn-risk items appear.',
  },
  {
    key: 'discord',
    name: 'Discord',
    icon: MessagesSquare,
    description: 'Get a ping in your Discord server on new feedback and decisions.',
  },
  {
    key: 'email_to_feedback',
    name: 'Email to Feedback',
    icon: Mail,
    description: 'Forward customer emails to auto-create feedback signals.',
  },
];

const ComingSoonCard = ({ item }) => {
  const storageKey = `vibesprint_wl_${item.key}`;
  const [joined, setJoined] = useState(false);
  useEffect(() => {
    if (localStorage.getItem(storageKey)) setJoined(true);
  }, [storageKey]);

  const join = async () => {
    try {
      await axios.post(`${API_URL}/api/waitlist`, {
        feature: item.key,
        visitor_id: getVisitorId(),
        source: 'integrations_page',
      });
      localStorage.setItem(storageKey, 'true');
      setJoined(true);
      toast.success(`You're on the ${item.name} waitlist`, {
        description: "We'll ping you the moment it ships.",
      });
    } catch (_e) {
      toast.error('Could not join the waitlist right now');
    }
  };

  const Icon = item.icon;
  return (
    <Card className="p-4 flex flex-col" data-testid={`integration-card-${item.key}`}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{item.name}</h3>
            <Badge variant="outline" className="gap-1 text-[10px] text-muted-foreground">
              <Lock className="h-3 w-3" /> Coming soon
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
        </div>
      </div>
      <div className="mt-4">
        {joined ? (
          <Badge variant="secondary" data-testid={`integration-joined-${item.key}`}>
            <Bell className="h-3 w-3 mr-1" /> On the waitlist
          </Badge>
        ) : (
          <Button variant="outline" size="sm" onClick={join} data-testid={`integration-connect-${item.key}`}>
            Connect
          </Button>
        )}
      </div>
    </Card>
  );
};

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const { currentWorkspaceId } = useWorkspace();
  const [activeTokens, setActiveTokens] = useState(0);
  const [suggestion, setSuggestion] = useState('');
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async () => {
    if (!currentWorkspaceId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/mcp/info`, {
        params: { workspace_id: currentWorkspaceId },
      });
      setActiveTokens(data.active_tokens || 0);
    } catch (_e) {
      /* non-blocking */
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  const submitSuggestion = async () => {
    const text = suggestion.trim();
    if (text.length < 2) {
      toast.error('Tell us which integration you want');
      return;
    }
    try {
      setSuggesting(true);
      await axios.post(`${API_URL}/api/waitlist`, {
        feature: `suggest_${text}`.slice(0, 60).toLowerCase(),
        visitor_id: getVisitorId(),
        source: 'integration_suggestion',
      });
      setSuggestion('');
      toast.success('Thanks — suggestion logged', {
        description: "We prioritise integrations by demand.",
      });
    } catch (_e) {
      toast.error('Could not submit suggestion right now');
    } finally {
      setSuggesting(false);
    }
  };

  const mcpConnected = activeTokens > 0;
  const usedCount = 1; // Agent Access (MCP) is the one live connection
  const totalCount = 1 + COMING_SOON.length;

  return (
    <SettingsLayout
      title="Integrations"
      description="Connect VibeSprint to the agents and tools where you already work."
    >
      <div className="space-y-8" data-testid="integrations-page">
        {/* Your accounts */}
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Your accounts
          </h2>
          <Card className="p-4" data-testid="integration-card-mcp">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <Bot className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold">Agent Access (MCP)</h3>
                  {mcpConnected ? (
                    <Badge variant="secondary" className="gap-1 text-emerald-600" data-testid="mcp-status-connected">
                      <CheckCircle2 className="h-3 w-3" /> Connected
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="text-primary" data-testid="mcp-status-available">
                      Available
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect Claude, Cursor, ChatGPT, Copilot, Gemini or any MCP client to read
                  feedback, run AI triage, move decisions and publish changelogs — right from your agent.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  {mcpConnected
                    ? `${activeTokens} active token${activeTokens === 1 ? '' : 's'} connected to this workspace.`
                    : 'No tokens yet — create one to connect your first agent.'}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate('/settings/api-tokens')} data-testid="mcp-manage-tokens">
                    Manage tokens & setup
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </section>

        <Separator />

        {/* Available integrations */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Available integrations
            </h2>
            <span className="text-xs text-muted-foreground" data-testid="integrations-usage-line">
              {usedCount} of {totalCount} integrations used on your plan
            </span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {COMING_SOON.map((item) => (
              <ComingSoonCard key={item.key} item={item} />
            ))}

            {/* Suggest an integration */}
            <Card className="p-4 border-dashed sm:col-span-2" data-testid="integration-suggest-card">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold">Suggest an integration</h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-3">
                    Tell us what to connect next — we prioritise by demand.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="e.g. Linear, Intercom, Notion…"
                      value={suggestion}
                      onChange={(e) => setSuggestion(e.target.value)}
                      maxLength={50}
                      onKeyDown={(e) => e.key === 'Enter' && submitSuggestion()}
                      data-testid="integration-suggest-input"
                    />
                    <Button
                      variant="outline"
                      onClick={submitSuggestion}
                      disabled={suggesting}
                      data-testid="integration-suggest-btn"
                    >
                      <Plus className="h-4 w-4 mr-1.5" /> Suggest
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </div>
    </SettingsLayout>
  );
};

export default IntegrationsPage;
